import { useState, useEffect, useRef } from 'react'
import type { Store } from '@/types'

const OSRM_BASE_URL = 'https://osrm-api.nextorder.co/route/v1/driving'
const WALKING_SPEED_M_PER_MIN = 83.33 // ~5 km/h

interface RouteResult {
  distance: number
  duration: number
}

async function fetchRouteDistance(
  storeLat: number,
  storeLong: number,
  userLat: number,
  userLong: number,
): Promise<RouteResult | null> {
  try {
    const url = `${OSRM_BASE_URL}/${storeLong},${storeLat};${userLong},${userLat}`
    const res = await fetch(url)
    if (!res.ok) return null
    const data = await res.json()
    if (data.code === 'Ok' && data.routes?.[0]) {
      return {
        distance: data.routes[0].distance,
        duration: data.routes[0].duration,
      }
    }
    return null
  } catch {
    return null
  }
}

function getUserLocation(): Promise<{ lat: number; long: number }> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported'))
      return
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, long: pos.coords.longitude }),
      (err) => reject(err),
      { enableHighAccuracy: true, timeout: 10000 },
    )
  })
}

/**
 * Fetches the user's geolocation, calls the OSRM routing API for each store,
 * and returns stores enriched with `distance` (meters) and `travelTime` (walking minutes).
 */
export function useStoreDistances(stores: Store[]): { stores: Store[]; loadingDistances: boolean } {
  const [enrichedStores, setEnrichedStores] = useState<Store[]>(stores)
  const [loadingDistances, setLoadingDistances] = useState(false)
  const cacheRef = useRef<Map<string, RouteResult>>(new Map())

  const storeIds = stores.map(s => s.storeId).join(',')

  useEffect(() => {
    if (!stores.length) {
      setEnrichedStores([])
      return
    }

    let cancelled = false
    setLoadingDistances(true)

    async function compute() {
      let userLocation: { lat: number; long: number }
      try {
        userLocation = await getUserLocation()
      } catch {
        if (!cancelled) {
          setEnrichedStores(stores)
          setLoadingDistances(false)
        }
        return
      }

      const results = await Promise.all(
        stores.map(async (store): Promise<Store> => {
          if (!store.addressLocation?.lat || !store.addressLocation?.long) return store

          const cacheKey = `${store.storeId}_${userLocation.lat}_${userLocation.long}`
          let route = cacheRef.current.get(cacheKey) ?? null

          if (!route) {
            route = await fetchRouteDistance(
              store.addressLocation.lat,
              store.addressLocation.long,
              userLocation.lat,
              userLocation.long,
            )
            if (route) {
              cacheRef.current.set(cacheKey, route)
            }
          }

          if (!route) return store

          return {
            ...store,
            distance: route.distance,
            travelTime: Math.round(route.distance / WALKING_SPEED_M_PER_MIN),
          }
        }),
      )

      if (!cancelled) {
        setEnrichedStores(results)
        setLoadingDistances(false)
      }
    }

    compute()
    return () => { cancelled = true }
  }, [storeIds]) // eslint-disable-line react-hooks/exhaustive-deps

  return { stores: enrichedStores, loadingDistances }
}
